import { z } from 'zod';

export const PURCHASE_EMAIL_REQUIRED_MESSAGE =
  'Use the email address where you received your Onefinity order confirmation or invoice.';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];

const isValidFileType = (file: File) =>
  ACCEPTED_IMAGE_TYPES.includes(file.type);

const profilePictureFileSchema = z
  .custom<File>((value) => value instanceof File)
  .refine(
    (file) => file.size <= MAX_FILE_SIZE,
    `File size should be less than 5MB.`
  )
  .refine(
    (file) => isValidFileType(file),
    'Only .jpg, .jpeg, .png and .webp formats are supported.'
  );

const profilePictureUrlSchema = z
  .string()
  .url()
  .refine(async (url) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const contentType = response.headers.get('content-type');
      return contentType ? ACCEPTED_IMAGE_TYPES.includes(contentType) : false;
    } catch {
      return false;
    }
  }, 'Invalid image URL or unsupported image type.');

export const loginSchema = z.object({
  email: z
    .string({
      required_error: 'Email is required',
    })
    .email({
      message: 'Please enter a valid email address',
    }),
  password: z.string().min(8, {
    message: 'Password must be at least 8 characters long',
  }),
});

const RESERVED_NAMES = [
  'onefinity',
  'filefinity',
  'masso',
  'redline',
  'vetric',
  'admin',
  'support',
  'official',
  'staff',
  'moderator',
];

const notReserved = (val: string) =>
  !RESERVED_NAMES.includes(val.trim().toLowerCase());

export const usernameSchema = z
  .string({
    required_error: 'Username is required',
  })
  .trim()
  .min(4, {
    message: 'Username must be at least 4 characters long',
  })
  .max(15, {
    message: 'Username must not exceed 15 characters',
  })
  .regex(/^[a-zA-Z0-9_]+$/, {
    message: 'Username can only contain letters, numbers, and underscores',
  })
  .regex(/^[a-zA-Z]/, {
    message: 'Username must start with a letter',
  })
  .regex(/^(?!.*_{2,}).*$/, {
    message: 'Username cannot contain consecutive underscores',
  })
  .refine((val) => !/\s/.test(val), {
    message: 'Username cannot contain spaces or other whitespace characters',
  })
  .refine(notReserved, { message: 'This username is reserved' });

const passwordField = (requiredError: string) =>
  z
    .string({ required_error: requiredError })
    .trim()
    .min(8, { message: 'Password must be at least 8 characters long' });

const passwordFields = {
  password: passwordField('Password is required'),
  confirmPassword: passwordField('Confirm password is required'),
};

const passwordsMatch = {
  message: 'Passwords must match',
  path: ['confirmPassword'],
};

export const signupBasicDetailsWithEmailSchema = z
  .object({
    profilePicture: z
      .union([profilePictureFileSchema, profilePictureUrlSchema])
      .optional(),
    username: usernameSchema,
    firstname: z
      .string({
        required_error: 'Firstname is required',
      })
      .trim()
      .min(1, {
        message: 'Firstname is required',
      })
      .refine(notReserved, { message: 'This name is reserved' }),
    lastname: z
      .string({
        required_error: 'Lastname is required',
      })
      .trim()
      .min(1, {
        message: 'Lastname is required',
      })
      .refine(notReserved, { message: 'This name is reserved' }),
    email: z
      .string({
        required_error: 'Email is required',
      })
      .trim()
      .email({
        message: 'Please enter a valid email address',
      }),
    ...passwordFields,
  })
  .refine((data) => data.password === data.confirmPassword, passwordsMatch);

export const signupBasicDetailsWithGoogleSchema = z
  .object({
    profilePicture: z
      .union([profilePictureFileSchema, profilePictureUrlSchema])
      .optional(),
    username: usernameSchema,
    firstname: z
      .string({
        required_error: 'Firstname is required',
      })
      .trim()
      .min(1, {
        message: 'Firstname is required',
      })
      .refine(notReserved, { message: 'This name is reserved' }),
    lastname: z
      .string({
        required_error: 'Lastname is required',
      })
      .trim()
      .min(1, {
        message: 'Lastname is required',
      })
      .refine(notReserved, { message: 'This name is reserved' }),
    email: z
      .string({
        required_error: 'Email is required',
      })
      .trim()
      .email({
        message: 'Please enter a valid email address',
      }),
    ...passwordFields,
  })
  .refine((data) => data.password === data.confirmPassword, passwordsMatch);

export const signupOtherDetailsSchema = z.object({
  locatedAt: z.string().min(1, {
    message: 'Located at is required',
  }),
  cncBrand: z.array(z.string()).min(1, {
    message: 'At least one CNC Brand is required',
  }),
  cncModel: z.array(z.string()).min(1, {
    message: 'At least one CNC Model is required',
  }),
  cncVariant: z.array(z.string()).min(1, {
    message: 'At least one CNC Variant is required',
  }),
});

export const forgetPasswordSchema = z.object({
  email: z.string().email(),
});

export const resetPasswordSchema = z
  .object({
    ...passwordFields,
  })
  .refine((data) => data.password === data.confirmPassword, passwordsMatch);

export const changePasswordSchema = z
  .object({
    currentPassword: passwordField('Password is required'),
    newPassword: passwordField('Password is required'),
    confirmPassword: passwordField('Confirm password is required'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, passwordsMatch);

export const editProfileFormSchema = z.object({
  firstname: z
    .string()
    .min(2, { message: 'First name must be at least 2 characters' })
    .max(50, { message: 'First name must not exceed 50 characters' })
    .refine(notReserved, { message: 'This name is reserved' }),
  lastname: z
    .string()
    .min(2, { message: 'Last name must be at least 2 characters' })
    .max(50, { message: 'Last name must not exceed 50 characters' })
    .refine(notReserved, { message: 'This name is reserved' }),
  profilePicture: z
    .union([
      profilePictureFileSchema,
      z.string().url({ message: 'Please enter a valid URL' }),
    ])
    .optional()
    .or(z.literal('')),
  socialLinks: z
    .object({
      website: z.string().max(500).optional(),
      youtube: z.string().max(500).optional(),
      instagram: z.string().max(500).optional(),
      facebook: z.string().max(500).optional(),
      tiktok: z.string().max(500).optional(),
      x: z.string().max(500).optional(),
    })
    .optional(),
  privacy: z
    .object({
      email: z.boolean(),
      firstname: z.boolean(),
      lastname: z.boolean(),
      links: z.boolean(),
    })
    .optional(),
  notificationPreferences: z
    .object({
      coinsEarned: z.boolean(),
      newPosts: z.boolean(),
    })
    .optional(),
});

export const addMachineSchema = z.object({
  cncBrand: z.array(z.string()).min(1, {
    message: 'At least one CNC Brand is required',
  }),
  cncModel: z.array(z.string()).min(1, {
    message: 'At least one CNC Model is required',
  }),
  cncVariant: z.array(z.string()).min(1, {
    message: 'At least one CNC Variant is required',
  }),
});
